/**
 * Create Layer Modal
 *
 * Dynamic form for creating/editing layers with type-specific configuration.
 * Part of Layer Drawer 2.0 - Phase 3 (Admin UI).
 *
 * Features:
 * - Type-specific forms (WMS, WFS, TMS, Live, GeoJSON)
 * - Field validation
 * - Preview functionality (validates layer by rendering on map)
 */

import React, { useState, useEffect } from 'react';
import {
  Layer,
  CreateLayerInput,
  LayerType,
  LayerCategory,
  WMSLayerConfig,
  WFSLayerConfig,
  TMSLayerConfig,
  LiveGeoJSONLayerConfig,
} from '../../../../types/layer';
import { LayerService } from '../../../../services/layerService';
import { LayerPreview } from '../components/LayerPreview';
import { IconPicker } from '../components/IconPicker';
import AppModal from '../../../common/AppModal';
import './CreateLayerModal.scss';

interface CreateLayerModalProps {
  layer?: Layer | null;
  onClose: (success: boolean) => void;
}

export const CreateLayerModal: React.FC<CreateLayerModalProps> = ({ layer, onClose }) => {
  const layerService = new LayerService();
  const isEditing = !!layer;

  // Form state
  const [formData, setFormData] = useState<Partial<CreateLayerInput>>({
    name: layer?.name || '',
    type: layer?.type || 'wms',
    category: layer?.category || 'overlays',
    layer_group: layer?.layer_group || '',
    tags: layer?.tags || [],
    source_url: layer?.source_url || '',
    legend_url: layer?.legend_url || '',
    zoom_min: layer?.zoom_min ?? 0,
    zoom_max: layer?.zoom_max ?? 22,
    default_opacity: layer?.default_opacity ?? 1.0,
    default_order: layer?.default_order ?? 0,
    enabled: layer?.enabled ?? true,
    config: layer?.config || {},
    metadata: layer?.metadata || {},
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tagsInput, setTagsInput] = useState(
    Array.isArray(formData.tags) ? formData.tags.join(', ') : ''
  );
  const [existingGroups, setExistingGroups] = useState<string[]>([]);
  const [showCustomGroup, setShowCustomGroup] = useState(false);

  // Fetch existing groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const layers = await layerService.getAllLayers();
        const groups = [...new Set(
          layers
            .map((l: Layer) => l.layer_group)
            .filter((g: string | null | undefined): g is string => !!g && g.trim() !== '')
        )].sort() as string[];
        setExistingGroups(groups);

        // If editing and the layer has a group not in the list, show custom input
        if (layer?.layer_group && !groups.includes(layer.layer_group)) {
          setShowCustomGroup(true);
        }
      } catch (err) {
        console.error('Failed to fetch groups:', err);
      }
    };
    fetchGroups();
  }, []);

  // Update tags when tagsInput changes
  useEffect(() => {
    const tagsArray = tagsInput
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    setFormData((prev) => ({ ...prev, tags: tagsArray }));
  }, [tagsInput]);

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Layer name is required';
    }

    if (!formData.type) {
      newErrors.type = 'Layer type is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.zoom_min !== undefined && (formData.zoom_min < 0 || formData.zoom_min > 22)) {
      newErrors.zoom_min = 'Zoom min must be between 0 and 22';
    }

    if (formData.zoom_max !== undefined && (formData.zoom_max < 0 || formData.zoom_max > 22)) {
      newErrors.zoom_max = 'Zoom max must be between 0 and 22';
    }

    if (
      formData.zoom_min !== undefined &&
      formData.zoom_max !== undefined &&
      formData.zoom_min > formData.zoom_max
    ) {
      newErrors.zoom_max = 'Zoom max must be greater than or equal to zoom min';
    }

    if (
      formData.default_opacity !== undefined &&
      (formData.default_opacity < 0 || formData.default_opacity > 1)
    ) {
      newErrors.default_opacity = 'Opacity must be between 0 and 1';
    }

    // Type-specific validation
    if (formData.type === 'wms' && !formData.config?.layers) {
      newErrors.config_layers = 'WMS layer name is required';
    }

    if (formData.type === 'wfs' && !formData.config?.typeName) {
      newErrors.config_typeName = 'WFS type name is required';
    }

    if (formData.type === 'tms' && !formData.config?.tileUrl) {
      newErrors.config_tileUrl = 'Tile URL is required';
    }

    // LiveGeoJSONLayer falls back to source_url when config.dataUrl is unset
    // (see src/easymap/layers2/LiveGeoJSONLayer.ts:347), so the catalog has
    // legitimate geojson layers like Current Wildfires (All) where the URL
    // lives in the top-level source_url column. Accept either.
    if (formData.type === 'geojson' && !formData.config?.dataUrl && !formData.source_url) {
      newErrors.config_dataUrl = 'Data URL is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isEditing && layer) {
        await layerService.updateLayer(layer.id, formData);
      } else {
        await layerService.createLayer(formData as CreateLayerInput);
      }

      onClose(true);
    } catch (error: any) {
      console.error('Error saving layer:', error);
      setErrors({ submit: error.message || 'Failed to save layer' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle layer deletion
   */
  const handleDelete = async () => {
    if (!layer) return;

    setLoading(true);

    try {
      await layerService.deleteLayer(layer.id);
      onClose(true);
    } catch (error: any) {
      console.error('Error deleting layer:', error);
      setErrors({ submit: error.message || 'Failed to delete layer' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  /**
   * Render the Refresh / Polling field block. Common to every layer type
   * the polling system supports (wms / wfs / tms / geojson / live / var).
   * Stored value is milliseconds; the input shows minutes.
   */
  const renderRefreshPollingFields = () => {
    const config = formData.config || {};
    return (
      <div className="form-section">
        <h3>Refresh / Polling</h3>
        <div className="form-group">
          <label>Refresh Interval (minutes)</label>
          <input
            type="number"
            min="0"
            step="1"
            placeholder="5"
            value={config.refreshInterval ? Math.round(config.refreshInterval / 60000) : ''}
            onChange={(e) => {
              const minutes = parseInt(e.target.value);
              setFormData({
                ...formData,
                config: {
                  ...config,
                  refreshInterval: minutes > 0 ? minutes * 60000 : undefined,
                },
              });
            }}
          />
          <small className="form-help">
            How often the layer refreshes. Leave blank for static layers. After changing, toggle the layer off and back on for the new interval to take effect.
          </small>
        </div>
        <div className="form-group">
          <label>Refresh Time Range (NWT)</label>
          <input
            type="text"
            placeholder="13:30-14:00"
            value={config.refreshTimeRange || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                config: { ...config, refreshTimeRange: e.target.value.trim() || undefined },
              })
            }
          />
          <small className="form-help">
            Optional. <code>HH:MM-HH:MM</code> in NWT (America/Yellowknife). When set, auto-refresh only fires while the current NWT time is in this window. Supports cross-midnight ranges (e.g. <code>22:00-06:00</code>).
          </small>
        </div>
      </div>
    );
  };

  /**
   * Render the Vector Style block. Shared by every vector-rendering layer
   * type (wfs, geojson, and any future vector tile type). Operates directly
   * on `config.style.{type, paint, icon, haloWidth, haloColor, shadowBlur,
   * shadowColor}` — same shape both consumers already use.
   */
  const renderVectorStyleFields = () => {
    const config = formData.config || {};
    const style: any = config.style || { type: 'circle', paint: {} };
    const styleType: string = style.type || 'circle';
    const paint: Record<string, any> = style.paint || {};

    const updateStyle = (updates: Record<string, any>) => {
      setFormData({
        ...formData,
        config: {
          ...config,
          style: { ...style, paint: { ...paint, ...updates } },
        },
      });
    };

    const updateStyleType = (newType: string) => {
      let defaultPaint: Record<string, any> = {};
      let defaultIcon = '';
      if (newType === 'circle') {
        defaultPaint = {
          'circle-radius': 6,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        };
      } else if (newType === 'symbol') {
        defaultPaint = {
          'icon-color': '#3b82f6',
          'icon-size': 1.5,
          'icon-opacity': 1,
        };
        defaultIcon = 'map-marker-alt';
      } else if (newType === 'line') {
        defaultPaint = {
          'line-color': '#3b82f6',
          'line-width': 2,
          'line-opacity': 0.8,
        };
      } else if (newType === 'fill') {
        defaultPaint = {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.5,
          'fill-outline-color': '#1e40af',
        };
      }
      setFormData({
        ...formData,
        config: {
          ...config,
          style: { type: newType, paint: defaultPaint, icon: defaultIcon || undefined },
        },
      });
    };

    const updateIcon = (iconName: string) => {
      setFormData({
        ...formData,
        config: { ...config, style: { ...style, icon: iconName || undefined } },
      });
    };

    return (
      <div className="style-customization-section">
        <h5>Layer Style</h5>
        <div className="form-group">
          <label>Geometry Type</label>
          <select value={styleType} onChange={(e) => updateStyleType(e.target.value)}>
            <option value="circle">Point (Circle)</option>
            <option value="symbol">Point (Icon)</option>
            <option value="line">Line</option>
            <option value="fill">Polygon (Fill)</option>
          </select>
        </div>

        {styleType === 'circle' && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Fill Color</label>
                <input type="color" value={paint['circle-color'] || '#3b82f6'}
                  onChange={(e) => updateStyle({ 'circle-color': e.target.value })} />
              </div>
              <div className="form-group">
                <label>Stroke Color</label>
                <input type="color" value={paint['circle-stroke-color'] || '#ffffff'}
                  onChange={(e) => updateStyle({ 'circle-stroke-color': e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Radius: {paint['circle-radius'] || 6}px</label>
                <input type="range" min="1" max="20" step="1"
                  value={paint['circle-radius'] || 6}
                  onChange={(e) => updateStyle({ 'circle-radius': parseInt(e.target.value) })} />
              </div>
              <div className="form-group">
                <label>Stroke Width: {paint['circle-stroke-width'] || 2}px</label>
                <input type="range" min="0" max="10" step="1"
                  value={paint['circle-stroke-width'] || 2}
                  onChange={(e) => updateStyle({ 'circle-stroke-width': parseInt(e.target.value) })} />
              </div>
            </div>
            <div className="form-group">
              <label>Opacity: {((paint['circle-opacity'] || 0.8) * 100).toFixed(0)}%</label>
              <input type="range" min="0" max="1" step="0.05"
                value={paint['circle-opacity'] || 0.8}
                onChange={(e) => updateStyle({ 'circle-opacity': parseFloat(e.target.value) })} />
            </div>
          </>
        )}

        {styleType === 'symbol' && (
          <>
            <div className="form-group">
              <label>Icon</label>
              <IconPicker value={style.icon || 'map-marker-alt'} onChange={updateIcon}
                color={paint['icon-color'] || '#3b82f6'} />
            </div>
            <div className="form-group">
              <label>Icon Color</label>
              <input type="color" value={paint['icon-color'] || '#3b82f6'}
                onChange={(e) => updateStyle({ 'icon-color': e.target.value })} />
            </div>
            <div className="form-group">
              <label>Icon Size: {paint['icon-size'] || 1.5}x</label>
              <input type="range" min="0.5" max="3" step="0.25"
                value={paint['icon-size'] || 1.5}
                onChange={(e) => updateStyle({ 'icon-size': parseFloat(e.target.value) })} />
            </div>
            <div className="form-group">
              <label>Opacity: {((paint['icon-opacity'] || 1) * 100).toFixed(0)}%</label>
              <input type="range" min="0" max="1" step="0.05"
                value={paint['icon-opacity'] || 1}
                onChange={(e) => updateStyle({ 'icon-opacity': parseFloat(e.target.value) })} />
            </div>

            <div className="form-group-divider"><span>Outline/Halo (Accessibility)</span></div>
            <div className="form-group">
              <label>Halo Width: {style.haloWidth || 0}px</label>
              <input type="range" min="0" max="5" step="0.5"
                value={style.haloWidth || 0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setFormData((prev) => ({
                    ...prev,
                    config: {
                      ...prev.config,
                      style: {
                        ...(prev.config as any)?.style,
                        haloWidth: value,
                        haloColor: (prev.config as any)?.style?.haloColor || '#ffffff',
                      },
                    },
                  }));
                }} />
            </div>
            {(style.haloWidth || 0) > 0 && (
              <div className="form-group">
                <label>Halo Color</label>
                <input type="color" value={style.haloColor || '#ffffff'}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      config: {
                        ...prev.config,
                        style: { ...(prev.config as any)?.style, haloColor: e.target.value },
                      },
                    }));
                  }} />
              </div>
            )}
            <div className="form-group">
              <label>Drop Shadow: {style.shadowBlur || 0}px</label>
              <input type="range" min="0" max="5" step="0.5"
                value={style.shadowBlur || 0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setFormData((prev) => ({
                    ...prev,
                    config: {
                      ...prev.config,
                      style: {
                        ...(prev.config as any)?.style,
                        shadowBlur: value,
                        shadowColor: (prev.config as any)?.style?.shadowColor || 'rgba(0,0,0,0.5)',
                      },
                    },
                  }));
                }} />
            </div>
            {(style.shadowBlur || 0) > 0 && (
              <div className="form-group">
                <label>Shadow Color</label>
                <input type="color"
                  value={style.shadowColor?.startsWith('rgba') ? '#000000' : (style.shadowColor || '#000000')}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      config: {
                        ...prev.config,
                        style: { ...(prev.config as any)?.style, shadowColor: e.target.value },
                      },
                    }));
                  }} />
              </div>
            )}
          </>
        )}

        {styleType === 'line' && (
          <>
            <div className="form-group">
              <label>Line Color</label>
              <input type="color" value={paint['line-color'] || '#3b82f6'}
                onChange={(e) => updateStyle({ 'line-color': e.target.value })} />
            </div>
            <div className="form-group">
              <label>Line Width: {paint['line-width'] || 2}px</label>
              <input type="range" min="1" max="10" step="1"
                value={paint['line-width'] || 2}
                onChange={(e) => updateStyle({ 'line-width': parseInt(e.target.value) })} />
            </div>
            <div className="form-group">
              <label>Opacity: {((paint['line-opacity'] || 0.8) * 100).toFixed(0)}%</label>
              <input type="range" min="0" max="1" step="0.05"
                value={paint['line-opacity'] || 0.8}
                onChange={(e) => updateStyle({ 'line-opacity': parseFloat(e.target.value) })} />
            </div>
          </>
        )}

        {styleType === 'fill' && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Fill Color</label>
                <input type="color" value={paint['fill-color'] || '#3b82f6'}
                  onChange={(e) => updateStyle({ 'fill-color': e.target.value })} />
              </div>
              <div className="form-group">
                <label>Outline Color</label>
                <input type="color" value={paint['fill-outline-color'] || '#1e40af'}
                  onChange={(e) => updateStyle({ 'fill-outline-color': e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Fill Opacity: {((paint['fill-opacity'] || 0.5) * 100).toFixed(0)}%</label>
              <input type="range" min="0" max="1" step="0.05"
                value={paint['fill-opacity'] || 0.5}
                onChange={(e) => updateStyle({ 'fill-opacity': parseFloat(e.target.value) })} />
            </div>
          </>
        )}
      </div>
    );
  };

  /**
   * Render type-specific configuration form
   */
  const renderTypeSpecificForm = () => {
    const config = formData.config || {};

    switch (formData.type) {
      case 'wms':
        return (
          <div className="type-specific-form">
            <h4>WMS Configuration</h4>
            <div className="form-group">
              <label>
                Layer Name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="workspace:layername"
                value={config.layers || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, layers: e.target.value },
                  })
                }
              />
              {errors.config_layers && <span className="error">{errors.config_layers}</span>}
            </div>
            <div className="form-group">
              <label>Styles</label>
              <input
                type="text"
                placeholder="default"
                value={config.styles || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, styles: e.target.value },
                  })
                }
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Format</label>
                <select
                  value={config.format || 'image/png'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, format: e.target.value },
                    })
                  }
                >
                  <option value="image/png">PNG</option>
                  <option value="image/jpeg">JPEG</option>
                  <option value="image/gif">GIF</option>
                </select>
              </div>
              <div className="form-group">
                <label>Version</label>
                <select
                  value={config.version || '1.3.0'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, version: e.target.value },
                    })
                  }
                >
                  <option value="1.3.0">1.3.0</option>
                  <option value="1.1.1">1.1.1</option>
                </select>
              </div>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={config.transparent ?? true}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, transparent: e.target.checked },
                    })
                  }
                />
                Transparent
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label title="Adds TILED=true to the WMS GetMap request so GeoServer serves from its tile cache.">
                <input
                  type="checkbox"
                  aria-label="Tiled"
                  checked={config.tiled === true}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, tiled: e.target.checked },
                    })
                  }
                />
                Tiled (GeoServer tile-cache hint)
              </label>
            </div>
          </div>
        );

      case 'wfs':
        return (
          <div className="type-specific-form">
            <h4>WFS Configuration</h4>
            <div className="form-group">
              <label>
                Type Name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="workspace:typename"
                value={config.typeName || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, typeName: e.target.value },
                  })
                }
              />
              {errors.config_typeName && <span className="error">{errors.config_typeName}</span>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Version</label>
                <select
                  value={config.version || '2.0.0'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, version: e.target.value },
                    })
                  }
                >
                  <option value="2.0.0">2.0.0</option>
                  <option value="1.1.0">1.1.0</option>
                </select>
              </div>
              <div className="form-group">
                <label>Max Features</label>
                <input
                  type="number"
                  placeholder="1000"
                  value={config.maxFeatures || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, maxFeatures: parseInt(e.target.value) || undefined },
                    })
                  }
                />
              </div>
            </div>
            <div className="form-group">
              <label>CQL Filter (Optional)</label>
              <input
                type="text"
                placeholder="status='active'"
                value={config.cqlFilter || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, cqlFilter: e.target.value },
                  })
                }
              />
              <small className="field-hint">
                Filter features using CQL syntax. Examples:
                <br />
                • <code>status='active'</code> - Text equality
                <br />
                • <code>temperature &gt; 20</code> - Numeric comparison
                <br />
                • <code>name LIKE '%fire%'</code> - Text pattern matching
                <br />
                • <code>status='active' AND temperature &gt; 30</code> - Multiple conditions
              </small>
            </div>
            {renderVectorStyleFields()}
          </div>
        );

      case 'tms':
        return (
          <div className="type-specific-form">
            <h4>TMS Configuration</h4>
            <div className="form-group">
              <label>
                Tile URL <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="https://example.com/tiles/{z}/{x}/{y}.png"
                value={config.tileUrl || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, tileUrl: e.target.value },
                  })
                }
              />
              {errors.config_tileUrl && <span className="error">{errors.config_tileUrl}</span>}
              <small>Use placeholders: {'{z}'}, {'{x}'}, {'{y}'}</small>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Tile Size</label>
                <input
                  type="number"
                  placeholder="256"
                  value={config.tileSize || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, tileSize: parseInt(e.target.value) || undefined },
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>Scheme</label>
                <select
                  value={config.scheme || 'xyz'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: { ...config, scheme: e.target.value },
                    })
                  }
                >
                  <option value="xyz">XYZ</option>
                  <option value="tms">TMS</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Attribution</label>
              <input
                type="text"
                placeholder="© Example"
                value={config.attribution || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, attribution: e.target.value },
                  })
                }
              />
            </div>
          </div>
        );

      case 'geojson': {
        return (
          <div className="type-specific-form">
            <h4>Live GeoJSON Configuration</h4>
            <div className="form-group">
              <label>
                Data URL <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="https://api.example.com/geojson"
                value={config.dataUrl || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, dataUrl: e.target.value },
                  })
                }
              />
              {errors.config_dataUrl && <span className="error">{errors.config_dataUrl}</span>}
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={config.cluster?.enabled ?? false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      config: {
                        ...config,
                        cluster: { ...config.cluster, enabled: e.target.checked },
                      },
                    })
                  }
                />
                Enable Clustering
              </label>
            </div>
            {config.cluster?.enabled && (
              <div className="form-row">
                <div className="form-group">
                  <label>Cluster Radius</label>
                  <input
                    type="number"
                    placeholder="50"
                    value={config.cluster?.radius || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...config,
                          cluster: {
                            ...config.cluster,
                            radius: parseInt(e.target.value) || undefined,
                          },
                        },
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Max Zoom</label>
                  <input
                    type="number"
                    placeholder="14"
                    value={config.cluster?.maxZoom || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...config,
                          cluster: {
                            ...config.cluster,
                            maxZoom: parseInt(e.target.value) || undefined,
                          },
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}

            {renderVectorStyleFields()}
          </div>
        );
      }

      case 'var':
        return (
          <div className="type-specific-form">
            <h4>VAR (Values at Risk) Configuration</h4>
            <div className="form-group">
              <label>Filter Mode</label>
              <select
                value={config.filterMode || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: {
                      ...config,
                      filterMode: e.target.value || undefined,
                    },
                  })
                }
              >
                <option value="">All VARs</option>
                <option value="red_flag">Red Flag only</option>
                <option value="sprinkler">Sprinkler only</option>
              </select>
              <small className="form-help">
                Restrict the layer to a subset of VARs. Source URL drives which API is hit; this is the in-memory filter applied to the response.
              </small>
            </div>
          </div>
        );

      case 'live':
        return (
          <div className="type-specific-form">
            <h4>Live Layer Configuration</h4>
            <div className="form-group">
              <label>Provider</label>
              <select
                value={config.providerId || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, providerId: e.target.value || undefined },
                  })
                }
              >
                <option value="">— select provider —</option>
                <option value="weather-stations">Live Weather (weather-stations)</option>
                <option value="aff">Live Aircraft (aff)</option>
              </select>
              <small className="form-help">
                Provider IDs are registered by LayerDrawerProvider on map mount. To add a new provider, register it in <code>layerDrawer/LayerDrawerProvider.tsx</code> first.
              </small>
            </div>
            <div className="form-group">
              <label>Icon (FontAwesome class)</label>
              <input
                type="text"
                placeholder="fa-solid fa-cloud-bolt"
                value={config.icon || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, icon: e.target.value || undefined },
                  })
                }
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                placeholder="Real-time weather station data"
                value={config.description || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: { ...config, description: e.target.value || undefined },
                  })
                }
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AppModal
      isOpen={true}
      onClose={() => onClose(false)}
      width={800}
      maxHeight="90vh"
      header={isEditing ? 'Edit Layer' : 'Create New Layer'}
      persistent
    >
      <div className="create-layer-modal">
        <form onSubmit={handleSubmit} className="create-layer-form">
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>

            <div className="form-group">
              <label>
                Layer Name <span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="My Layer"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {errors.name && <span className="error">{errors.name}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Type <span className="required">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value as LayerType;
                    // Auto-set category to 'backgrounds' for mapbox-style layers
                    const newCategory = newType === 'mapbox-style' ? 'backgrounds' : formData.category;
                    setFormData({ ...formData, type: newType, category: newCategory, config: {} });
                  }}
                  disabled={isEditing}
                >
                  <option value="wms">WMS</option>
                  <option value="wfs">WFS</option>
                  <option value="tms">TMS</option>
                  <option value="geojson">GeoJSON</option>
                  <option value="var">VAR (Values at Risk)</option>
                  <option value="live">Live (Provider-backed)</option>
                  <option value="mapbox-style">Mapbox Style (Background)</option>
                </select>
                {errors.type && <span className="error">{errors.type}</span>}
              </div>

              <div className="form-group">
                <label>
                  Category <span className="required">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value as LayerCategory })
                  }
                >
                  <option value="backgrounds">Backgrounds</option>
                  <option value="overlays">Overlays</option>
                  <option value="live">Live Data</option>
                </select>
                {errors.category && <span className="error">{errors.category}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Group</label>
              {showCustomGroup ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter new group name"
                    value={formData.layer_group || ''}
                    onChange={(e) => setFormData({ ...formData, layer_group: e.target.value })}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomGroup(false);
                      setFormData({ ...formData, layer_group: '' });
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      background: 'var(--em3-color-background-panel, #f0f0f0)',
                      border: '1px solid var(--em3-color-border, #ccc)',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <select
                  value={formData.layer_group || ''}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setShowCustomGroup(true);
                      setFormData({ ...formData, layer_group: '' });
                    } else {
                      setFormData({ ...formData, layer_group: e.target.value });
                    }
                  }}
                >
                  <option value="">No group</option>
                  {existingGroups.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                  <option value="__new__">+ Create new group...</option>
                </select>
              )}
              <small>Organize layers into groups for easier browsing</small>
            </div>

            <div className="form-group">
              <label>Tags</label>
              <input
                type="text"
                placeholder="wildfire, regions, 2024"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
              />
              <small>Comma-separated tags</small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enabled ?? true}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                />
                Enabled by Default
              </label>
              {formData.category === 'backgrounds' && formData.enabled && (
                <small className="hint-warning" style={{ color: '#856404', display: 'block', marginTop: '4px' }}>
                  Only one background can be the default. Enabling this will automatically disable other backgrounds.
                </small>
              )}
            </div>
          </div>

          {/* Type-Specific Configuration */}
          <div className="form-section">{renderTypeSpecificForm()}</div>

          {/* Refresh / Polling (shared across all polling-capable layer types) */}
          {renderRefreshPollingFields()}

          {/* Layer Properties */}
          <div className="form-section">
            <h3>Layer Properties</h3>

            <div className="form-group">
              <label>
                {formData.type === 'mapbox-style' ? 'Style URL' : 'Source URL'}
              </label>
              <input
                type="text"
                placeholder={
                  formData.type === 'mapbox-style'
                    ? 'mapbox://styles/mapbox/streets-v11'
                    : 'https://geoserver.example.com/wms'
                }
                value={formData.source_url || ''}
                onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
              />
              {formData.type === 'mapbox-style' && (
                <span className="hint">
                  Examples: mapbox://styles/mapbox/streets-v11, mapbox://styles/mapbox/satellite-v9, mapbox://styles/mapbox/dark-v10
                </span>
              )}
            </div>

            <div className="form-group">
              <label>Legend URL</label>
              <input
                type="text"
                placeholder="https://geoserver.example.com/legend.png"
                value={formData.legend_url || ''}
                onChange={(e) => setFormData({ ...formData, legend_url: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Min Zoom</label>
                <input
                  type="number"
                  min="0"
                  max="22"
                  placeholder="0"
                  value={formData.zoom_min ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, zoom_min: parseInt(e.target.value) || 0 })
                  }
                />
                {errors.zoom_min && <span className="error">{errors.zoom_min}</span>}
              </div>

              <div className="form-group">
                <label>Max Zoom</label>
                <input
                  type="number"
                  min="0"
                  max="22"
                  placeholder="22"
                  value={formData.zoom_max ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, zoom_max: parseInt(e.target.value) || 22 })
                  }
                />
                {errors.zoom_max && <span className="error">{errors.zoom_max}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Default Opacity</label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  placeholder="1.0"
                  value={formData.default_opacity ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, default_opacity: parseFloat(e.target.value) || 1.0 })
                  }
                />
                {errors.default_opacity && <span className="error">{errors.default_opacity}</span>}
              </div>

              <div className="form-group">
                <label>Default Order</label>
                <input
                  type="number"
                  placeholder="0"
                  value={formData.default_order ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, default_order: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
            </div>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="form-error-message">
              <strong>Error:</strong> {errors.submit}
            </div>
          )}

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </button>

            {isEditing && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
              >
                Delete Layer
              </button>
            )}

            <button
              type="button"
              className="btn btn-neutral"
              onClick={() => setShowPreview(true)}
              disabled={loading}
            >
              Preview
            </button>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEditing ? 'Update Layer' : 'Create Layer'}
            </button>
          </div>
        </form>

        {/* Layer Preview */}
        {showPreview && (
          <LayerPreview
            layerConfig={formData as any}
            onClose={() => setShowPreview(false)}
          />
        )}

      </div>

      {/* Delete Confirmation Dialog */}
      <AppModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        width={400}
        header="Delete Layer?"
      >
        <div className="confirmation-dialog">
          <p>
            Are you sure you want to delete <strong>{formData.name}</strong>?
            <br />
            This action cannot be undone.
          </p>
          <div className="confirmation-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </AppModal>
    </AppModal>
  );
};
